import { useEffect, useState, useRef } from "react";
import axios from "axios";
import "@/App.css";

// Use same-origin requests so this works on any domain the app is served
// from (preview, emergent.host production, or custom domain like
// codex.dealiq.co.nz). The Kubernetes ingress routes any "/api/*" path on the
// same hostname to the FastAPI backend.
const API = "/api";

axios.defaults.withCredentials = true;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function CodexView() {
  // Load the protected codex via fetch with credentials, then render inline.
  // We inject the HTML into the page instead of iframe-ing /api/codex so that
  // its own scripts (D3, event handlers) run in the top window and CSS fills
  // the viewport exactly like the standalone file.
  const hostRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/codex`, { credentials: "include" });
        if (!res.ok) {
          window.location.reload();
          return;
        }
        const html = await res.text();
        if (cancelled) return;
        // Replace the entire document so codex.html owns <html>/<head>/<body>.
        document.open();
        document.write(html);
        document.close();
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      ref={hostRef}
      data-testid="codex-loading"
      style={{
        position: "fixed", inset: 0, display: "flex",
        alignItems: "center", justifyContent: "center",
        background: "#0D1B2A", color: "#AAFF00",
        fontFamily: "Manrope, system-ui, sans-serif", fontSize: 14,
        letterSpacing: 2, textTransform: "uppercase",
      }}
    >
      Loading codex…
    </div>
  );
}

function Gate({ onUnlocked }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/auth/unlock`, { password }, { withCredentials: true });
      onUnlocked();
    } catch (err) {
      const msg = err?.response?.status === 429
        ? "Too many attempts. Try again in 15 minutes."
        : formatApiErrorDetail(err?.response?.data?.detail) || "Incorrect password.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="codex-gate"
      style={{
        minHeight: "100vh", width: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "#0D1B2A",
        fontFamily: "Manrope, system-ui, sans-serif",
        color: "#fff", padding: "24px",
      }}
    >
      <form
        onSubmit={submit}
        data-testid="codex-gate-form"
        style={{
          width: "100%", maxWidth: 420,
          background: "#091d30",
          border: "1px solid rgba(170,255,0,0.22)",
          borderRadius: 14,
          padding: "32px 28px 28px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{
          fontSize: 11, letterSpacing: 3, textTransform: "uppercase",
          color: "#AAFF00", marginBottom: 6, fontWeight: 700,
        }}>
          deal iQ
        </div>
        <h1 style={{
          fontSize: 22, fontWeight: 700, margin: "0 0 8px",
          letterSpacing: "-0.01em", lineHeight: 1.2,
        }}>
          Cognitive Bias Codex
        </h1>
        <p style={{
          fontSize: 13, color: "#8699ad", margin: "0 0 24px", lineHeight: 1.5,
        }}>
          Enter the access password to continue.
        </p>

        <label
          htmlFor="codex-password"
          style={{ fontSize: 11, letterSpacing: 1.6, textTransform: "uppercase", color: "#8699ad", fontWeight: 700 }}
        >
          Password
        </label>
        <input
          id="codex-password"
          data-testid="codex-password-input"
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          style={{
            width: "100%", marginTop: 8, marginBottom: 16,
            padding: "12px 14px",
            background: "#0D1B2A",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 8,
            color: "#fff", fontSize: 15,
            fontFamily: "inherit",
            outline: "none",
            transition: "border-color 0.15s",
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "#AAFF00")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)")}
        />

        {error && (
          <div
            data-testid="codex-gate-error"
            style={{
              marginBottom: 14,
              padding: "10px 12px",
              borderRadius: 8,
              background: "rgba(255,80,80,0.08)",
              border: "1px solid rgba(255,80,80,0.3)",
              color: "#ff9b9b", fontSize: 13, lineHeight: 1.4,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !password}
          data-testid="codex-gate-submit"
          style={{
            width: "100%", padding: "12px 16px",
            borderRadius: 999, border: "none",
            background: loading ? "#6e8a00" : "#AAFF00",
            color: "#0D1B2A", fontWeight: 700, fontSize: 12,
            letterSpacing: 1.6, textTransform: "uppercase",
            cursor: loading || !password ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            boxShadow: "0 10px 24px rgba(0,0,0,0.45)",
            transition: "transform 0.12s, background 0.15s",
          }}
        >
          {loading ? "Unlocking…" : "Unlock"}
        </button>
      </form>
    </div>
  );
}

export default function App() {
  const [state, setState] = useState("checking"); // "checking" | "locked" | "unlocked"

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/status`, { withCredentials: true });
        setState(data.authenticated ? "unlocked" : "locked");
      } catch {
        setState("locked");
      }
    })();
  }, []);

  if (state === "checking") {
    return (
      <div
        data-testid="codex-boot"
        style={{
          minHeight: "100vh",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "#0D1B2A", color: "#6a7a8a",
          fontFamily: "Manrope, system-ui, sans-serif", fontSize: 13,
          letterSpacing: 1.6, textTransform: "uppercase",
        }}
      >
        Loading…
      </div>
    );
  }

  if (state === "unlocked") return <CodexView />;
  return <Gate onUnlocked={() => setState("unlocked")} />;
}
